import React, { useState } from "react";
import { ArrowLeft, Clock, FileText, User, Loader2 } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useBulkPaymentBatchDetails } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from "recharts";
import { PageHeader } from "@shared/components/PageHeader";
import { Tabs } from "@shared/ui/tabs";
import { TimelineTab } from "../../shared/TimelineTab";
import { RecordsTab } from "./tabComponents/records/records";
import { useToast } from "@shared/hooks/use-toast";

export const BatchDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: batchData, isLoading } = useBulkPaymentBatchDetails(Number(id));
  const [batchStatus, setBatchStatus] = useState("In Progress");
  const { toast } = useToast();

  React.useEffect(() => {
    if (batchData?.statusDisplay) {
      setBatchStatus(batchData.statusDisplay);
    }
  }, [batchData?.statusDisplay]);

  const tabComponents = {
    records: {
      label: "Records",
      component: <RecordsTab data={batchData} />,
    },
    auditTrail: {
      label: "Audit Trail",
      component: (
        <div className="pt-4">
          <TimelineTab
            remoteUtilityId={remoteUtilityId?.toString() || ""}
            requestId={batchData?.batchId || ""}
            objectType="bulk_batch"
            module="cx"
          />
        </div>
      ),
    },
  };

  const urlMapping = {
    records: "records",
    auditTrail: "audit-trail",
  };

  return (
    <div className="space-y-6 p-2">
      <div className="mb-4 flex items-center gap-4">
        {/* Back Arrow */}
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/cx/payments/bulk-payment/create")}
        />

        {/* Page Header */}
        <div className="flex flex-col">
          <PageHeader
            title="View Batch Details"
            subtitle="Track performance and progress of your bulk payment batch"
          />
        </div>
      </div>

      {/* File Info Card */}
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm relative">
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-amber-600"></div>
        <div className="p-5 pl-7">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-bold text-gray-900">
                {batchData?.fileName || "Loading..."}
              </h1>
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  batchStatus === "Completed" ||
                  batchStatus.includes("Completed")
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {batchStatus}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 font-medium">
              <div className="flex items-center gap-1.5">
                <FileText className="h-4 w-4" />
                {batchData?.batchId || "N/A"}
              </div>
              <div className="flex items-center gap-1.5">
                <User className="h-4 w-4" />
                {batchData?.uploadedBy || "N/A"}
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />-
              </div>
              <div>
                Format:{" "}
                <span className="text-gray-900">
                  {batchData?.sourceSummary?.format || "N/A"}
                </span>
              </div>
              <div>
                Payment Source:{" "}
                <span className="text-gray-900">
                  {batchData?.sourceSummary?.paymentSource || "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar Card */}
      <div className="bg-green-100/50 border border-green-200 rounded-xl p-5 relative overflow-hidden">
        <div className="flex justify-between items-start mb-4">
          <div className="flex gap-4 items-start">
            <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center border border-green-200 shrink-0">
              {isLoading ? (
                <Loader2 className="animate-spin h-5 w-5 text-green-600" />
              ) : (
                <FileText className="h-5 w-5 text-green-600" />
              )}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 leading-none mb-1.5">
                {batchStatus.includes("Completed")
                  ? "Processing complete"
                  : "Processing..."}
              </h2>
              <p className="text-sm text-gray-600 font-medium">
                {batchData?.matchedCount || 0} matched ·{" "}
                {batchData?.unmatchedCount || 0} unmatched ·{" "}
                {batchData?.duplicateCount || 0} duplicates ·{" "}
                {batchData?.failedCount || 0} failed
              </p>
            </div>
          </div>
          <div className="text-xl font-bold text-green-700">-%</div>
        </div>

        <div className="h-4 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={[{ name: "Progress", value: 100 }]}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <XAxis type="number" domain={[0, 100]} hide />
              <YAxis dataKey="name" type="category" hide />
              <Bar
                dataKey="value"
                fill="#16a34a"
                background={{ fill: "#bbf7d0" }}
                radius={[4, 4, 4, 4]}
                barSize={8}
                isAnimationActive={true}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        defaultValue="records"
        className="w-full"
        tabsListClassName="grid grid-cols-2"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        level={0}
      />
    </div>
  );
};

export default BatchDetails;
